import { useState } from "react";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import Calling from "../../api/Calling";
import { useNavigate } from "react-router-dom";
import { FiUser, FiLock, FiEye, FiEyeOff, FiDownload } from "react-icons/fi";

export default function Login() {
    const [loader, setLoader] = useState(false);
    const [adminId, setAdminId] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoader(true);
        const body = {
            a_id: adminId,
            a_password: password
        };
        const resp = await Calling.AdminLogin(body);
        const { status, token, message } = resp;
        if (status) {
            toast.success(message, {
                autoClose: 2000,
            });
            setTimeout(() => {
                localStorage.setItem("token", token);
                navigate("/admin/panel/login/otp");
                setLoader(false);
            }, 1000);
        } else {
            toast.error(message, {
                autoClose: 2000,
            });
            setLoader(false);
        }
    };

    return (
        <>
            {loader && <Loader />}
            <title>Admin Login | Video Download</title>

            <div className="min-h-screen w-full flex items-center justify-center bg-main-bg relative overflow-hidden">
                {/* Background Decorative Elements */}
                <div className="absolute top-0 left-0 w-72 h-72 bg-main-blue/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-main-blue/10 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl"></div>
                <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-main-blue/5 rounded-full blur-2xl"></div>

                <div className="w-full max-w-md mx-4 relative z-10">
                    {/* Logo/Brand Section */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-main-blue to-red-600 rounded-2xl shadow-lg shadow-main-blue/30 mb-4">
                            <FiDownload className="text-main-white text-3xl" />
                        </div>
                        <h1 className="text-2xl font-bold text-main-font">Video Download</h1>
                        <p className="text-main-subfont text-sm mt-1">Admin Control Panel</p>
                    </div>

                    {/* Login Card */}
                    <div className="bg-main-white rounded-2xl shadow-xl shadow-main-blue/10 border border-main-border/50 overflow-hidden">
                        {/* Card Header */}
                        <div className="bg-gradient-to-r from-main-blue to-red-500 px-6 py-5">
                            <h2 className="text-xl font-semibold text-main-white">Welcome Back</h2>
                            <p className="text-white/80 text-sm mt-1">Sign in to continue to dashboard</p>
                        </div>

                        {/* Form Section */}
                        <form onSubmit={handleSubmit} className="p-6 space-y-5">
                            {/* Admin ID Field */}
                            <div>
                                <label htmlFor="AdminId" className="block text-sm font-medium text-main-font mb-2">
                                    Admin ID
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FiUser className="text-main-subfont" />
                                    </div>
                                    <input
                                        type="text"
                                        id="AdminId"
                                        value={adminId}
                                        onChange={(e) => setAdminId(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border border-main-border rounded-xl bg-main-bg text-main-font placeholder-main-subfont focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue transition-all duration-200 outline-none"
                                        placeholder="Enter your admin ID"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Password Field */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label htmlFor="password" className="block text-sm font-medium text-main-font">
                                        Password
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/admin/panel/forgetpw')}
                                        className="text-sm text-main-blue hover:text-red-600 font-medium transition-colors"
                                    >
                                        Forgot Password?
                                    </button>
                                </div>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FiLock className="text-main-subfont" />
                                    </div>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full pl-10 pr-12 py-3 border border-main-border rounded-xl bg-main-bg text-main-font placeholder-main-subfont focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue transition-all duration-200 outline-none"
                                        placeholder="Enter your password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-main-subfont hover:text-main-font transition-colors"
                                    >
                                        {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                    </button>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={loader}
                                className="w-full bg-gradient-to-r from-main-blue to-red-500 text-main-white py-3.5 px-4 rounded-xl font-semibold hover:from-red-600 hover:to-main-blue focus:outline-none focus:ring-2 focus:ring-main-blue/50 focus:ring-offset-2 transition-all duration-300 shadow-lg shadow-main-blue/25 hover:shadow-main-blue/40 disabled:opacity-70 disabled:cursor-not-allowed transform hover:-translate-y-0.5 active:translate-y-0"
                            >
                                {loader ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Signing in...
                                    </span>
                                ) : (
                                    "Sign In"
                                )}
                            </button>
                        </form>

                        {/* Footer */}
                        <div className="px-6 py-4 bg-main-th/50 border-t border-main-border/50">
                            <p className="text-center text-sm text-main-subfont">
                                Protected by secure authentication
                            </p>
                        </div>
                    </div>

                    {/* Bottom Text */}
                    <p className="text-center text-main-subfont text-sm mt-6">
                        © {new Date().getFullYear()} Video Download Admin Panel
                    </p>
                </div>
            </div>
        </>
    );
}